<script setup>
import Text from "~/components/atoms/Text.vue";
import BaseForm from "~/components/organisms/Checkout/BaseForm.vue";
import {useCartStore} from "~/store/cartStore";
import CheckoutBlock from "~/components/templates/CheckoutBlock.vue";
import CartProducts from "~/components/organisms/Cart/CartProducts.vue";
import ProductPrice from "~/components/molecules/ProductPrice.vue";
import Button from "~/components/atoms/Button.vue";
import Radiobox from "~/components/atoms/Radiobox.vue";
import KlarnaIcon from "~/components/icons/KlarnaIcon.vue";
import MastercardIcon from "~/components/icons/MastercardIcon.vue";
import VisaIcon from "~/components/icons/VisaIcon.vue";
import EnvelopeCartIcon from "~/components/icons/EnvelopeCartIcon.vue";
import FormInput from "~/components/atoms/FormInput.vue";
import LabelIcon from "~/components/icons/LabelIcon.vue";
import TrashIcon from "~/components/icons/TrashIcon.vue";
import ShareIcon from "~/components/icons/ShareIcon.vue";
import CopyIcon from "~/components/icons/CopyIcon.vue";
import {computed} from "vue";
import {useRoute, useRouter} from "vue-router";
import Checkbox from "~/components/atoms/Checkbox.vue";
import ServicePointPicker from "~/components/organisms/Checkout/ServicePointPicker.vue";
import Modal from "~/components/molecules/Modal.vue";
import {useStartupStore} from "~/store/sturtupStore.js";
import Heading from "~/components/atoms/Heading.vue";
import CartIcon from "~/components/icons/CartIcon.vue";
import CheckIcon from "~/components/icons/CheckIcon.vue";
import SpoilerTabs from "~/components/molecules/SpoilerTabs.vue";
import Help from "~/components/molecules/Help.vue";
import ExpandIcon from "~/components/icons/ExpandIcon.vue";
import {useCurrencyStore} from "~/store/currencyStore.js";
import Disclaimer from "~/components/organisms/Checkout/Disclaimer.vue";
import BetterWith from "~/components/organisms/BetterWith.vue";

definePageMeta({
  layout: 'checkout',
  ssr: false
});

useSeoMeta({
  title: 'Checkout - Hill Ceramic',
  description: 'Slutför ditt köp - Checkout',
})

useHead({
  meta: [
    {name: 'robots', content: 'noindex, nofollow'}
  ],
})

const router = useRouter();
const route = useRoute();
const config = useRuntimeConfig();

const {t} = useI18n();

const cartStore = useCartStore();
const checkoutTaxRate = computed(() => Number(cartStore.content?.tax_rate) || 1.25)
const showZeroTax = computed(() => {
  const rate = checkoutTaxRate.value
  return Number.isFinite(rate) && rate <= 1
})
const totalWithTax = computed(() => Number(cartStore.content?.total_sum) || 0)
const discountAmount = computed(() => Number(cartStore.content?.discount) || 0)
const totalWithTaxBeforeDiscount = computed(() => totalWithTax.value + discountAmount.value)
const chargedTotal = (gross) => {
  const rate = checkoutTaxRate.value
  if (!gross) {
    return 0
  }
  if (rate >= 1.25) {
    return gross
  }
  return Math.round(gross / 1.25 * rate * 100) / 100
}
const includedTax = (gross) => {
  const charged = chargedTotal(gross)
  const rate = checkoutTaxRate.value
  if (!charged || rate <= 1) {
    return 0
  }
  return charged - charged / rate
}
const startupStore = useStartupStore();

const couponMode = ref(false);
const commentMode = ref(!!cartStore?.content?.client_data?.comment);
const butchMode = ref(!!cartStore?.content?.client_data?.batch_number);
const shareCartMode = ref(false);
const coupon = ref('');
const shareCartEmail = ref('');
const shareCartUrl = ref('');
const pickSP = ref(false);
const isSubmitting = ref(false);

watch(
  () => cartStore.content?.coupon,
  (value) => {
    coupon.value = value || ''
  },
  { immediate: true }
)

const validateEmail = () => {
  const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
  return pattern.test(shareCartEmail.value)
}

const ombudIsAvailable = () => {
  if (!cartStore.initialized || !cartStore.content?.agent_is_available) {
    return false
  }

  const countryId = cartStore.content?.client_data?.country
  const country = startupStore.countries?.find((item) => item.id == countryId)
  const code = country?.code?.toUpperCase()

  if (code === 'SE' || code === 'DK' || code === 'NO') {
    return true
  }

  const locale = config.public.locale

  if (locale === 'sv' && countryId == 1) {
    return true
  }

  if (locale === 'da' && countryId == 2) {
    return true
  }

  if ((locale === 'nb' || locale === 'no') && countryId == 3) {
    return true
  }

  return false
}

const selectOmbud = () => {
  cartStore.saveShipping('parcel_ombud')

  const postcode = cartStore.content?.client_data?.zip?.toString().trim()

  if (postcode && !currentServiceLocation.value) {
    pickSP.value = true
  }
}


let lastFetchedZipCountry = null
let zipRequestId = 0

const assignNearestServiceLocation = () => {
  const requestId = ++zipRequestId

  cartStore.getServiceLocations().then((locations) => {
    if (requestId !== zipRequestId) {
      return
    }

    if (locations?.length) {
      cartStore.saveServicePoint(locations[0])
    } else if (cartStore.content.client_data.servicePoint) {
      cartStore.saveClientData('servicePoint', null)
    }
  })
}

const clearServiceLocation = () => {
  zipRequestId++
  lastFetchedZipCountry = null
  cartStore.serviceLocations = []

  if (cartStore.content.client_data.city) {
    cartStore.content.client_data.city = ''
  }

  if (cartStore.content.client_data.servicePoint) {
    cartStore.saveClientData('servicePoint', null);
  }
}

watch(() => {
  const zip = cartStore.content?.client_data?.zip?.toString().replace(/\s+/g, '') || ''
  const country = cartStore.content?.client_data?.country || ''

  return `${zip}|${country}`
}, (value) => {
  if (cartStore.zipIsValid && value !== lastFetchedZipCountry) {
    lastFetchedZipCountry = value
    assignNearestServiceLocation()
  } else if (!cartStore.zipIsValid) {
    clearServiceLocation()
  }
})

onMounted(() => {
  const onCartReady = () => {
    if (route.query?.hash) {
      const {hash, ...remainingQueries} = route.query;
      router.replace({ path: route.path, query: remainingQueries });
    }

    if (typeof (dataLayer) != "undefined" && dataLayer !== null) {
      dataLayer.push({ecommerce: null});
      dataLayer.push({
        event: "begin_checkout",
        'currency': 'SEK',
        'value': Math.ceil(cartStore.content.total_sum / useCurrencyStore().rate),
        ecommerce: {
          items: cartStore.content.products.map(product => {
            return {
              item_name: product.attributes.sku + ' - ' + product.name.replace(' - Varuprov'),
              item_id: product.attributes.sku,
              price: product.price,
              item_brand: product.attributes.supplier_name,
              item_category: product.attributes.group_category,
              quantity: product.quantity
            }
          })
        }
      });
    }
  }

  // Always reload on checkout so optional bundles (cart_supplementaries) are included.
  cartStore.getContent(route.query?.hash, route.query?.type, onCartReady, {includeSupplementaries: true})

  shareCartUrl.value = window.location.host + '/checkout/?hash=' + cartStore.getToken
})

const currentServiceLocation = computed(() => {
  if (!cartStore.content.client_data.servicePoint || !cartStore.zipIsValid) {
    return null
  }

  return JSON.parse(cartStore.content.client_data.servicePoint)
})

const createInvoiceToken = () => {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const baseFormRef = ref(null)

const submit = async () => {
  if (isSubmitting.value) {
    return
  }

  isSubmitting.value = true
  let redirected = false

  try {
    await baseFormRef.value?.flushDebouncedFields?.()

    if (cartStore.content.client_data.servicePoint && !currentServiceLocation.value) {
      cartStore.content.client_data.servicePoint = null
    }

    await cartStore.syncCheckoutClientData()

    if (cartStore.content.payment_method === 'invoice') {
      const invoiceToken = createInvoiceToken()
      const cartToken = cartStore.getCartToken()
      const params = new URLSearchParams({
        invoice_token: invoiceToken,
        cart_token: cartToken,
      })
      redirected = true
      window.location.assign(`/checkout/thank-you/?${params.toString()}`)
      return
    }

    await router.push('/checkout/payment/')
  } catch (e) {
    console.error('[Checkout] submit failed', {
      status: e?.response?.status,
      message: e?.message
    })
  } finally {
    if (!redirected) {
      isSubmitting.value = false
    }
  }
}

const btnClick = () => {
  if (isSubmitting.value) {
    return
  }
  document.querySelector('#checkout-form button').click()
  window.scrollTo(0, 0)
}

</script>

<template>
  <template v-if="cartStore.initialized">
    <template v-if="cartStore.content?.products?.length">
      <div class="row full-width g-48 bigger-gap flex-start column-mobile">
        <div class="mobile-cart" v-if="!$device.isDesktop">
          <SpoilerTabs class="top-spoiler" :tabs="[{title:t('cart.cart'), prefixComponent:CartIcon}]">
            <template #tab-content-0>
              <div style="background: white; padding: 15px;">
                <CartProducts>
                  <template #default="product">
                    <div class="product-calculator"
                         v-if="product.product.attributes.unit === 'm2' && !parseInt(product.product.attributes.sample)">
                      <SpoilerTabs class="calc-spoiler" :tabs="[t('checkout.details')]">
                        <template #tab-content-0>
                          <table class="table">
                            <tbody>
                            <tr>
                              <td>{{ $t('product.price_per') }} m2:</td>
                              <td>
                                <ProductPrice style="font-size: 12px;" :price="product.product.price" :light="true"/>
                              </td>
                              <td>
                                <div class="row g-4">
                                  ({{ $t('product.exclude_taxes') }}
                                  <div class="row">
                                    <ProductPrice style="font-size: 12px;" :price="product.product.price * 0.8"
                                                  :light="true"/>
                                    )
                                  </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>{{ $t('product.price_per') }} {{ $t('product.box') }}:</td>
                              <td>
                                <ProductPrice style="font-size: 12px;" :price="product.product.attributes.price_per_box"
                                              :light="true"/>
                              </td>
                              <td>
                                <div class="row g-4">
                                  ({{ $t('product.exclude_taxes') }}
                                  <div class="row">
                                    <ProductPrice style="font-size: 12px;"
                                                  :price="product.product.attributes.price_per_box * 0.8"
                                                  :light="true"/>
                                    )
                                  </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>m2/{{ $t('product.box') }}:</td>
                              <td>{{ product.product.attributes.unit_qty }}</td>
                              <td></td>
                            </tr>
                            </tbody>
                          </table>
                        </template>
                      </SpoilerTabs>
                    </div>
                  </template>
                </CartProducts>
              </div>
            </template>
          </SpoilerTabs>
        </div>

        <!--Left side-->
        <div class="g-48 bigger-gap sticky flexed">
          <BaseForm ref="baseFormRef" @submit="submit"/>
          <!--Delivery methods-->
          <div class="g-24">
            <CheckoutBlock :is-transparent="true" :title="t('checkout.delivery_method')"
                           :number="2">
              <div class="g-16">
                <!--Ombud-->
                <div v-if="ombudIsAvailable()"
                     @click="selectOmbud()"
                     class="option space-between full-width clickable g-16"
                     :class="{active:cartStore.content?.shipping_code === 'parcel_ombud'}">
                  <div class="row full-width space-between">
                    <div class="row no-flex g-8">
                      <div class="no-flex">
                        <Radiobox name="shipping"
                                  v-model="cartStore.content.shipping_code" value="parcel_ombud"/>

                      </div>
                      <Text>{{ $t('checkout.parcel_ombud_full') }} {{
                          cartStore.content?.client_data?.city ? $t('base.to') + ' ' + cartStore.content?.client_data?.city : ''
                        }}
                      </Text>
                    </div>
                    <ProductPrice v-if="cartStore.content.payment_method !== 'invoice'"
                                  :digits="0"
                                  :light-unit="cartStore.content?.shipping_code !== 'parcel_ombud'"
                                  :key="cartStore.content?.shipping_price_to_terminal"
                                  :price="cartStore.content?.shipping_price_to_terminal"/>
                    <Text v-else>{{ $t('checkout.quotation') }}</Text>
                  </div>
                  <div class="full-width clickable"
                       v-if="cartStore.content?.shipping_code === 'parcel_ombud' &&
                     (cartStore.content?.client_data.city || currentServiceLocation)"
                       @click.prevent.stop="">
                    <Text class="sp-title">{{ $t('checkout.delivery_to') }}</Text>
                    <Text class="sp-name" v-if="currentServiceLocation">
                      {{ currentServiceLocation.name }},{{ currentServiceLocation.street }},
                      {{ currentServiceLocation.cityName }}
                    </Text>
                    <Text @click="pickSP=true" underline class="clickable sp-edit">{{ $t('base.change') }}</Text>
                  </div>
                </div>
                <!--Home shipping-->
                <div class="option full-width clickable flex-center g-16"
                     :class="{active:cartStore.content?.shipping_code === 'home_shipping'}"
                     @click="cartStore.saveShipping('home_shipping')">
                  <div class="row full-width space-between">
                    <div class="row no-flex g-8">
                      <div class="no-flex">
                        <Radiobox name="shipping" v-model="cartStore.content.shipping_code" value="home_shipping"/>
                      </div>
                      <Text>{{ $t('checkout.home_shipping_full') }} {{
                          cartStore.content?.client_data?.city ? $t('base.to') + ' ' + cartStore.content?.client_data?.city : ''
                        }}
                      </Text>
                    </div>


                    <ProductPrice :digits="0" v-if="cartStore.content.payment_method !== 'invoice'"
                                  :light-unit="cartStore.content?.shipping_code !== 'home_shipping'"
                                  :key="cartStore.content?.shipping_price_to_home"
                                  :price="cartStore.content?.shipping_price_to_home"/>
                    <Text v-else>{{ $t('checkout.quotation') }}</Text>

                  </div>

                  <div class="full-width clickable sub-service"
                       :class="{active:!parseInt(cartStore.content.client_data.delivery_notification)}"
                       v-if="cartStore.content?.shipping_code === 'home_shipping'"
                       @click.prevent.stop="cartStore.saveClientData('delivery_notification', 0)">
                    <div class="row full-width space-between">
                      <div class="row no-flex g-12">
                        <div class="no-flex child-check">
                          <Radiobox value="0"
                                    :model-value="String(cartStore.content.client_data.delivery_notification ?? 0)"
                                    @update:model-value="cartStore.saveClientData('delivery_notification', 0)"/>
                          <!--                          <Checkbox-->
                          <!--                              name="notification"-->
                          <!--                              :checked="parseInt(cartStore.content.client_data.delivery_notification)"/>-->
                        </div>
                        <Text style="max-width: 80%;">
                          <Text font-weight="600">{{
                              $t('checkout.without_notification')
                            }}
                          </Text>
                          <Text tag="span" style="width: 80%;" font-weight="400" font-size="10px" class="n-desc">
                            ({{ $t('checkout.without_notification_description') }})
                          </Text>
                        </Text>
                      </div>
                      <div class="row">
                        <ProductPrice :digits="0"
                                      :light-unit="!parseInt(cartStore.content.client_data.delivery_notification)"
                                      :key="cartStore.content?.notification_price"
                                      :price="0"/>
                      </div>
                    </div>
                  </div>
                  <div class="full-width clickable sub-service"
                       :class="{active:parseInt(cartStore.content.client_data.delivery_notification)}"
                       v-if="cartStore.content?.shipping_code === 'home_shipping'"
                       @click.prevent.stop="cartStore.saveClientData('delivery_notification', 1)">
                    <div class="row full-width space-between">
                      <div class="row no-flex g-12">
                        <div class="no-flex child-check">
                          <Radiobox
                              value="1"
                              :model-value="String(cartStore.content.client_data.delivery_notification ?? 0)"
                              @update:model-value="cartStore.saveClientData('delivery_notification', 1)"/>
                          <!--                          <Checkbox-->
                          <!--                              name="notification"-->
                          <!--                              :checked="parseInt(cartStore.content.client_data.delivery_notification)"/>-->
                        </div>

                        <div class="notification-copy">
                          <div class="row g-8 notification-title">
                            <Text tag="span" font-weight="600">{{ $t('checkout.notification') }}</Text>
                            <a class="question-link" :href="$t('faq.delivery.link')" @click.stop>
                              <img alt="Question" src="/images/help/question.png">
                            </a>
                          </div>
                          <Text tag="span" font-weight="400" font-size="10px" class="n-desc">
                            ({{ $t('checkout.notification_description') }})
                          </Text>
                        </div>
                      </div>
                      <div class="row">
                        <Text tag="b">+</Text>
                        <ProductPrice :digits="0"
                                      :light-unit="!parseInt(cartStore.content.client_data.delivery_notification)"
                                      :key="cartStore.content?.notification_price"
                                      :price="cartStore.content?.notification_price"/>
                      </div>
                    </div>
                  </div>
                </div>
                <!--Pick up-->
                <div class="option space-between full-width g-16"
                     :class="{active:cartStore.content?.shipping_code === 'pick_up'}"
                     @click="cartStore.saveShipping('pick_up')">
                  <div class="row full-width space-between clickable">
                    <div class="row no-flex g-8">
                      <div class="no-flex">
                        <Radiobox name="shipping"
                                  v-model="cartStore.content.shipping_code" value="pick_up"/>
                      </div>
                      <Text>{{ $t('checkout.pick_up_full') }}</Text>
                      <Help>{{ $t('checkout.pick_up_text') }}
                        <nuxt-link target="_blank" style="color: white;" to="/faq/lager/">Läs mer</nuxt-link>
                        .
                      </Help>
                    </div>

                    <ProductPrice :light-unit="cartStore.content?.shipping_code !== 'pick_up'" :price="0" :digits="0"/>
                  </div>

                  <div class="full-width clickable sub-service"
                       :class="{active:parseInt(cartStore.content.client_data.delivery_notification)}"
                       v-if="cartStore.content?.shipping_code === 'pick_up'"
                       @click.prevent.stop="cartStore.saveClientData('truck_pick_up', parseInt(cartStore.content.client_data.truck_pick_up) ? 0 : 1)">
                    <div class="row full-width space-between">
                      <div class="row no-flex g-8">
                        <div class="no-flex child-check">
                          <Checkbox
                              name="notification"
                              :checked="parseInt(cartStore.content.client_data.truck_pick_up)"/>
                        </div>
                        <Text>{{ $t('checkout.will_pick_up') }}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CheckoutBlock>

            <div class="g-16">
              <Text @click="commentMode=!commentMode" tag="b" class="clickable expander">{{ commentMode ? '-' : '+' }}
                {{ $t("checkout.leave_comment") }}
              </Text>

              <!--Comment-->
              <div class="g-16" v-if="cartStore?.content?.client_data && commentMode">
                <CheckoutBlock>
                  <FormInput @change="cartStore.saveClientData('comment',cartStore?.content?.client_data?.comment)"
                             type="textarea" v-model="cartStore.content.client_data.comment"
                             :label="t('checkout.leave_order_comment')"/>
                </CheckoutBlock>
              </div>
            </div>

            <div class="g-16" v-if="commentMode">
              <Text @click="butchMode=!butchMode" tag="b" class="clickable expander">{{ butchMode ? '-' : '+' }}
                {{ $t('checkout.extension') }}
              </Text>

              <!--Batch-->
              <CheckoutBlock v-if="butchMode">
                <div class="g-16">
                  <Text>{{ $t('checkout.batch_text') }}</Text>
                  <FormInput type="text"
                             @change="cartStore.saveClientData('batch_number',cartStore.content.client_data.batch_number)"
                             v-model="cartStore.content.client_data.batch_number"
                             :label="$t('checkout.batch_label')"/>
                </div>
              </CheckoutBlock>
            </div>
          </div>

          <CheckoutBlock :title="t('product.optional')"
                         v-if="cartStore?.content?.cart_supplementaries?.bundles_by_id?.length" :number="3">
            <div style="overflow: auto" class="optional-products">
              <BetterWith :vertical="true" :no-padding="true"
                          ga-key="tillval_caclulator_checkout"
                          sku="123"
                          :items="cartStore?.content?.cart_supplementaries?.bundles_by_id.map((entry) => entry.bundle)"/>
            </div>
          </CheckoutBlock>

          <!--Payment methods-->
          <CheckoutBlock :is-transparent="true" :title="t('checkout.payment_method')" style="display: none;"
                         :number="cartStore?.content?.cart_supplementaries?.bundles_by_id?.length ? 4 : 3">
            <div class="g-16">
              <div v-if="cartStore.content.payment_methods.includes('klarna')"
                   class="row option space-between full-width clickable" style="font-weight: 500">
                <div class="no-flex row g-8">
                  <div>
                    <Radiobox name="payment" :checked="cartStore.content.payment_method === 'klarna'"/>
                  </div>
                  <Text>{{ $t('checkout.klarna_payment') }}</Text>
                </div>
                <div class="row no-flex g-8 payment-icons" style="max-width: 140px;flex-wrap: wrap; justify-content: center;">
                  <KlarnaIcon/>
                  <img src="/svg/apple-pay.svg" alt="Apple Pay"/>
                  <img src="/svg/google-pay.svg" alt="Google Pay"/>
                  <MastercardIcon/>
                  <VisaIcon/>
                </div>
              </div>

              <div v-if="cartStore.content.payment_methods.includes('invoice')"
                   class="row option space-between full-width clickable" style="font-weight: 500">
                <div class="no-flex row g-8">
                  <div>
                    <Radiobox name="payment" :checked="cartStore.content.payment_method === 'invoice'"/>
                  </div>
                  <Text> {{ $t('checkout.invoice_text') }}</Text>
                </div>
                <div class="row no-flex g-8">
                </div>
              </div>
            </div>
          </CheckoutBlock>
        </div>

        <!--Right side-->
        <div class="g-48 bigger-gap sticky">
          <!--Cart-->
          <CheckoutBlock :title="$t('cart.cart')" v-if="$device.isDesktop">
            <CartProducts>
              <template #default="product">
                <div class="product-calculator"
                     v-if="product.product.attributes.unit === 'm2' && !parseInt(product.product.attributes.sample)">
                  <SpoilerTabs class="calc-spoiler" :tabs="['Detaljerad prisinformation']">
                    <template #tab-content-0>
                      <table class="table">
                        <tbody>
                        <tr>
                          <td>{{ $t('product.price_per') }} m2:</td>
                          <td>
                            <ProductPrice style="font-size: 12px;" :price="product.product.price" :light="true"/>
                          </td>
                          <td>
                            <div class="row g-4">
                              ({{ $t('product.exclude_taxes') }}
                              <div class="row">
                                <ProductPrice style="font-size: 12px;" :price="product.product.price * 0.8"
                                              :light="true"/>
                                )
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>{{ $t('product.price_per') }} box:</td>
                          <td>
                            <ProductPrice style="font-size: 12px;" :price="product.product.attributes.price_per_box"
                                          :light="true"/>
                          </td>
                          <td>
                            <div class="row g-4">
                              ({{ $t('product.exclude_taxes') }}
                              <div class="row">
                                <ProductPrice style="font-size: 12px;"
                                              :price="product.product.attributes.price_per_box * 0.8" :light="true"/>
                                )
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>m2/{{ $t('product.box') }}:</td>
                          <td>{{ product.product.attributes.unit_qty }}</td>
                          <td></td>
                        </tr>
                        </tbody>
                      </table>
                    </template>
                  </SpoilerTabs>
                </div>
              </template>
            </CartProducts>
          </CheckoutBlock>

          <CheckoutBlock v-if="shareCartMode" @rightClick="shareCartMode=false"
                         :rightButton="{title:t('cart.share_cart'),icon:ShareIcon}">
            <div class="g-24">
              <div class="row full-width g-16">
                <div>
                  <FormInput v-model="shareCartEmail" :label="$t('cart.email')"/>
                </div>
                <div class="no-flex square-btn">
                  <Button :check-after-click="true" :timeless-check="true" :disabled="!validateEmail()"
                          @click="cartStore.share(shareCartEmail)"
                          class="tall">
                    <EnvelopeCartIcon stroke="white"/>
                  </Button>
                </div>
              </div>
              <div class="row full-width g-16">
                <div>
                  <FormInput v-model="shareCartUrl" disabled :label="$t('cart.share_url')"/>
                </div>
                <div class="no-flex square-btn">
                  <Button v-copy="cartStore.content.share_cart_url"
                          :check-after-click="true"
                          class="tall">
                    <CopyIcon stroke="white"/>
                  </Button>
                </div>
              </div>
            </div>
          </CheckoutBlock>

          <!--Total-->
          <CheckoutBlock :rightButton="!shareCartMode ? {title:t('cart.share_cart'),icon:ShareIcon} : false"
                         @rightClick="shareCartMode=true">
            <div class="g-24 the-same-gap">
              <div class="g-32">
                <Text v-if="!couponMode && !cartStore.content.coupon" class="clickable" @click="couponMode=true"
                      tag="strong">+ {{ $t('cart.add_coupon') }}
                </Text>
                <div class="g-8" v-if="couponMode || cartStore.content.coupon">
                  <div class="row full-width g-16">
                    <div>
                      <FormInput v-model="coupon" :label="$t('cart.add_coupon_2')"/>

                      <div class="success" v-if="cartStore.content.discount">
                        <CheckIcon width="20px" height="20px"/>
                      </div>
                    </div>
                    <div class="no-flex square-btn">
                      <Button v-if="cartStore.content.coupon" @click="cartStore.removeCoupon()"
                              class="tall">
                        <TrashIcon stroke="white"/>
                      </Button>
                      <Button v-else @click="cartStore.saveCoupon(coupon)" class="tall">
                        <LabelIcon stroke="white"/>
                      </Button>
                    </div>
                  </div>
                  <Text tag="b" v-if="cartStore.couponResponse" type="red">{{
                      cartStore.couponResponse
                    }}
                  </Text>
                </div>
                <div>
                  <div class="row full-width space-between">
                    <Text>{{ $t('cart.subtotal') }}:</Text>
                    <ProductPrice :key="cartStore.content?.sub_total_sum" class="sub-total"
                                  :price="cartStore.content?.sub_total_sum"/>
                  </div>
                  <div class="row full-width space-between">
                    <Text>{{ $t('checkout.' + cartStore.content.shipping_code) }}:</Text>
                    <ProductPrice v-if="cartStore.content.payment_method !== 'invoice'"
                                  :key="cartStore.content?.shipping_price" class="sub-total"
                                  :price="cartStore.content?.shipping_price"/>
                    <Text v-else>{{ $t('checkout.quotation') }}</Text>
                  </div>
                  <div class="row full-width space-between" v-if="cartStore.content?.feePrice">
                    <div class="row g-4">
                      <Text>{{ $t('cart.fee') }}:</Text>
                      <Help>{{ $t('cart.fee_hover') }}
                        <nuxt-link target="_blank" :to="$t('cart.fee_link')">{{ $t('base.read_more') }}</nuxt-link>
                      </Help>
                    </div>
                    <ProductPrice
                        :key="cartStore.content?.feePrice" class="sub-total"
                        :price="cartStore.content?.feePrice"/>
                  </div>
                </div>
              </div>
              <hr>
              <div class="g-8">
                <div v-if="cartStore.content.discount" class="row full-width space-between">
                  <Text>{{ $t('cart.coupon') }}:</Text>
                  <ProductPrice :red="true" :key="cartStore.content?.discount" class="sub-total red"
                                :price="cartStore.content?.discount * -1"/>
                </div>

                <div class="disclaimer"
                     v-if="cartStore.content?.products.filter(p=>p.discountType === '20mm campaign').length">
                  {{ $t('checkout.20mm_campaign') }}
                </div>
              </div>
              <div class="row full-width space-between">
                <Text class="total" tag="strong">{{ $t('base.total') }}:</Text>
                <div class="total-prices" :class="{ 'zero-tax-stack': showZeroTax }">
                  <template v-if="showZeroTax">
                    <ProductPrice
                        v-if="discountAmount"
                        type="old"
                        class="total-price"
                        :key="totalWithTaxBeforeDiscount + '-gross-before'"
                        :price="totalWithTaxBeforeDiscount"/>
                    <ProductPrice
                        type="old"
                        class="total-price"
                        :key="totalWithTax + '-gross'"
                        :price="totalWithTax"/>
                    <ProductPrice
                        :red="true"
                        class="total-price"
                        :key="totalWithTax + '-net-' + checkoutTaxRate"
                        :price="chargedTotal(totalWithTax)"/>
                    <Text type="red" class="zero-tax-label">(0% {{ $t('product.taxes') }})</Text>
                  </template>
                  <template v-else>
                    <ProductPrice :key="cartStore.content?.total_sum + '-' + checkoutTaxRate" class="total-price left-auto"
                                  :class="{old:cartStore.content.discount}"
                                  :price="chargedTotal((cartStore.content?.total_sum || 0) + (cartStore.content.discount || 0))"/>
                    <ProductPrice v-if="cartStore.content.discount" :key="cartStore.content?.total_sum + '-net-' + checkoutTaxRate"
                                  class="total-price left-auto"
                                  :red="true"
                                  :price="chargedTotal(cartStore.content?.total_sum)"/>
                    <Text class="taxes g-4">({{ $t('product.include') }}.
                      <ProductPrice :light-unit="true" class="taxes"
                                    :price="includedTax(cartStore.content?.total_sum)"/>
                      {{ $t('product.taxes') }})
                    </Text>
                  </template>
                </div>
              </div>


              <Button
                  @click="btnClick" type="black"
                  :loading="isSubmitting"
                  class="uppercase tall full-width pay">
                {{
                  cartStore.content.payment_method === 'invoice' ? t('quotation.send_quotation_international') : t('checkout.payment')
                }}
                <ExpandIcon stroke="white"/>
              </Button>
            </div>
          </CheckoutBlock>
        </div>

        <Modal :no-padding="true" :no-heading="true" v-if="pickSP && cartStore.serviceLocations?.length" @close="pickSP=false" :width="800" :height="currentServiceLocation ? 700 : 500">
          <ServicePointPicker :currentServiceLocation="currentServiceLocation" @close="pickSP=false"
                              :serviceLocations="cartStore.serviceLocations" :countries="startupStore.countries"/>
        </Modal>
      </div>
    </template>
    <template v-else>
      <br>
      <br>
      <br>
      <div class="empty-container text-center">
        <div class="g-64">
          <div class="g-32">
            <Heading tag="h1">Inga produkter i varukorgen</Heading>
            <Text>Du har inte lagt några varor i varukorgen än.</Text>
            <CartIcon class="center-auto" width="100px" height="100px"/>
          </div>
          <nuxt-link to="/">
            <Button type="black" class="uppercase full-width tall">{{ $t('checkout.go_shopping') }}</Button>
          </nuxt-link>
        </div>
      </div>
    </template>
  </template>
  <!--Main content-->
</template>


<style scoped lang="scss">

.optional-products {
  max-height: 352px;

  @include smd {
    max-height: 277px;
  }
}

.expander {
  @include mobile {
    font-size: 12px;
  }
}

.mobile-cart {
  width: 100%;

  .top-spoiler {
    &:deep(.tab-content) {
      padding: 0;
    }

    &:deep(.heading) {
      border-bottom: 1px solid rgba(233, 234, 240, 1);
      padding: 0;

      span {
        font-size: 16px;
        font-weight: 600;
      }

      div {
        svg {
          width: 28px;
          height: 28px;
          stroke-width: 1.5px;
          background: white;
          padding: 6px;
          border-radius: 100%;
        }
      }
    }
  }
}

.calc-spoiler:deep(.heading) {
  border: 0 !important;

  span {
    font-size: 12px !important;
    font-weight: 400 !important;
  }
}

.pay {
  font-size: 14px !important;
}

.product-calculator {
  margin-bottom: 16px;
  margin-top: 16px;

  &:deep(.heading) {
    padding: 0 !important;
    height: 16px !important;
    color: rgba(102, 104, 115, 1);
    font-weight: 600;

    &:hover {
      background: transparent !important;
    }
  }

  &:deep(.tab-content) {
    padding: 0 !important;
  }

  &:deep(.expander) {
    margin-left: 6px;
  }
}

.table {
  font-size: 12px;
  border-spacing: 0;

  @include mobile {
    font-size: 10px;
  }

  td:nth-child(3) {
    min-width: 120px;
  }
}

.success {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);

  svg {
    stroke: $green;
    height: 15px;
    width: 20px;
  }
}

.sticky {
  position: sticky;
  top: 0;
  max-width: 468px;
  width: 100%;

  &:first-child {
    max-width: 718px;
  }

  @include smd {
    position: relative;
    margin: auto;
    max-width: 718px;
  }
}

.option {
  background: $white;
  min-height: 58px;
  padding: 21px 32px;

  @include smd {
    padding: 15px;
    font-size: 12px;
  }

  .price {
    font-weight: 400;

    @include smd {
      font-size: 12px;
    }
  }

  &.active {
    font-weight: 500;

    .price {
      font-weight: 500;
    }
  }

  .payment-icons {
    svg,
    img {
      height: 18px;
      width: auto;
    }
  }
}

.link {
  font-size: 15px;
  color: $disabled-text;

  &.router-link-exact-active {
    color: $black;

    p {
      font-weight: 500;
    }
  }
}

.sub-total {
  font-weight: 600;
  font-size: 16px;
}

.total {
  font-size: 18px;
}

.total-prices {
  width: fit-content;
}

.total-prices.zero-tax-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;
  line-height: 1.2;
}

.total-price, .total-price.old {
  color: $dark-black;
  font-size: 22px;
}

.total-price.old {
  font-size: 18px !important;
}

.zero-tax-label {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.taxes {
  color: $light-black;
  font-weight: 400;
  font-size: 14px;
  width: fit-content;
  display: flex;
}

.square-btn {
  width: 77px;
}

.child-check {
  padding-left: 30px;
  max-width: 60px;
}

.sp-name {
  font-size: 12px;
}

.sp-edit, .sp-title {
  font-size: 11px;
  color: $light-black;
}

.notification-copy {
  flex: 1;
  min-width: 0;
  gap: 2px;
}

.notification-title {
  align-items: center;
}

.question-link {
  display: flex;
  flex: none;
  line-height: 0;

  img {
    width: 14px;
    height: 14px;
  }
}

.sub-service {
  font-weight: 400;

  &:deep(.price) {
    font-weight: 400 !important;
  }

  &.active {
    font-weight: 500;

    &:deep(.price) {
      font-weight: 500 !important;
    }
  }
}

.disclaimer {
  border: 1px dashed #18181F;
  padding: 16px;
}
</style>
